import React from "react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSubmit,
} from "~/react/ui/dropdown-menu"
import { Button } from "~/react/ui/button"
import { Checkbox } from "~/react/ui/checkbox"
import { CreatableSelect } from "~/react/ui/select"
import { Tag } from "~/react/ui/tag"

// Shared types
export type Department =
  | "executive"
  | "it"
  | "finance"
  | "management"
  | "sales"
  | "legal"
  | "support"
  | "hr"
  | "marketing"
  | "communication"
  | "education"
  | "design"
  | "health"
  | "operations"

export type VerificationStatus = "valid" | "accept_all" | "unknown"

export const departmentsList: Record<Department, string> = {
  executive: "Executive",
  it: "IT",
  finance: "Finance",
  management: "Management",
  sales: "Sales",
  legal: "Legal",
  support: "Support",
  hr: "HR",
  marketing: "Marketing",
  communication: "Communication",
  education: "Education",
  design: "Design",
  health: "Health",
  operations: "Operations",
}

export const verificationStatusList: Record<VerificationStatus, string> = {
  valid: "Valid",
  accept_all: "Accept all",
  unknown: "Unknown",
}

// Job Title Filter Component
interface JobTitleFilterProps {
  selectedJobTitles: string[]
  setSelectedJobTitles: (titles: string[]) => void
  jobTitles: string[]
  onApply: () => void
}

export const JobTitleFilter: React.FC<JobTitleFilterProps> = ({
  selectedJobTitles,
  setSelectedJobTitles,
  jobTitles,
  onApply,
}) => {
  const [jobTitleFilterInput, setJobTitleFilterInput] = React.useState<string>("")

  let jobTitlesOptions = []
  if (jobTitleFilterInput.length >= 1) {
    jobTitlesOptions = jobTitles
      .map((title) => {
        if (title.toLowerCase().includes(jobTitleFilterInput.toLowerCase())) {
          return {
            value: title,
            label: title,
          }
        }
        return null
      })
      .filter(Boolean)
  } else {
    jobTitlesOptions = jobTitles.slice(0, 100).map((title) => ({
      value: title,
      label: title,
    }))
  }

  return (
    <div className="h-filter">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-filter__button"
            size="sm"
            variant={selectedJobTitles.length > 0 ? "default" : "outline"}
          >
            {i18n.t("js.filters.job_title")}
            {selectedJobTitles.length > 0 && ` (${selectedJobTitles.length})`}
            <span className="far fa-angle-down" aria-hidden="true"></span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <CreatableSelect
            placeholder={i18n.t("js.filters.search_job_titles")}
            options={jobTitlesOptions}
            isMulti={true}
            className="overflow-visible"
            value={selectedJobTitles.map((title) => ({
              value: title,
              label: title,
            }))}
            onInputChange={(newValue) => {
              setJobTitleFilterInput(newValue)
            }}
            onChange={(e) => setSelectedJobTitles(e.map((item) => item.value))}
          />
          <div className="tags-list mt-2 mb-2">
            {selectedJobTitles.map((title) => (
              <Tag key={title}>
                {title}
                <span
                  className="far fa-times ml-1"
                  onClick={() =>
                    setSelectedJobTitles((prev) =>
                      prev.filter((item) => item !== title)
                    )
                  }
                />
              </Tag>
            ))}
          </div>
          <DropdownMenuSubmit onClick={onApply}>
            {i18n.t("js.filters.apply")}
          </DropdownMenuSubmit>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

// Verification Status Filter Component
interface VerificationStatusFilterProps {
  selectedVerificationStatus: VerificationStatus[]
  setSelectedVerificationStatus: (status: VerificationStatus[]) => void
  onApply: () => void
}

export const VerificationStatusFilter: React.FC<VerificationStatusFilterProps> = ({
  selectedVerificationStatus,
  setSelectedVerificationStatus,
  onApply,
}) => {
  return (
    <div className="h-filter">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-filter__button"
            size="sm"
            variant={selectedVerificationStatus.length > 0 ? "default" : "outline"}
          >
            {i18n.t("js.filters.verification_status.title")}
            {selectedVerificationStatus.length > 0 && ` (${selectedVerificationStatus.length})`}
            <span className="far fa-angle-down" aria-hidden="true"></span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {Object.entries(verificationStatusList).map(([key, value]) => (
            <DropdownMenuItem key={key}>
              <Checkbox
                checked={selectedVerificationStatus.includes(key as VerificationStatus)}
                id={`filter-verification-status-${key}`}
                name="filter-verification-status"
                onChange={() =>
                  setSelectedVerificationStatus((prev) =>
                    prev.includes(key as VerificationStatus)
                      ? prev.filter((item) => item !== (key as VerificationStatus))
                      : [...prev, key as VerificationStatus]
                  )
                }
              />
              <label htmlFor={`filter-verification-status-${key}`}>{value}</label>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSubmit onClick={onApply}>
            {i18n.t("js.filters.apply")}
          </DropdownMenuSubmit>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

// Department Filter Component
interface DepartmentFilterProps {
  selectedDepartments: Department[]
  setSelectedDepartments: (departments: Department[]) => void
  onApply: () => void
}

export const DepartmentFilter: React.FC<DepartmentFilterProps> = ({
  selectedDepartments,
  setSelectedDepartments,
  onApply,
}) => {
  return (
    <div className="h-filter" key="department">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-filter__button"
            size="sm"
            variant={selectedDepartments.length > 0 ? "default" : "outline"}
          >
            {i18n.t("js.filters.department")}
            {selectedDepartments.length > 0 && ` (${selectedDepartments.length})`}
            <span className="far fa-angle-down" aria-hidden="true"></span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {Object.entries(departmentsList).map(([key, value]) => (
            <DropdownMenuItem key={key}>
              <Checkbox
                checked={selectedDepartments.includes(key as Department)}
                id={`filter-department-${key}`}
                name="filter-department"
                onChange={() =>
                  setSelectedDepartments((prev) =>
                    prev.includes(key as Department)
                      ? prev.filter((item) => item !== (key as Department))
                      : [...prev, key as Department]
                  )
                }
              />
              <label htmlFor={`filter-department-${key}`}>{value}</label>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSubmit onClick={onApply}>
            {i18n.t("js.filters.apply")}
          </DropdownMenuSubmit>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}